"use client";

import type { SagraListing } from "@/types/sagra";
import { haversineDistance } from "@/lib/geo";

/**
 * Carica l'indice globale via fetch (client-side).
 * Versione client-safe di loadGlobalNearbyIndex.
 */
async function loadGlobalNearbyIndexClient(): Promise<SagraListing[]> {
  try {
    const response = await fetch("/nearby-index.json", { cache: "no-store" });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data) ? (data as SagraListing[]) : [];
  } catch {
    return [];
  }
}

/**
 * Filtra eventi entro raggioKm e nei prossimi daysAhead giorni (intervallo [oggi, oggi+daysAhead]).
 * Versione client-safe che usa fetch invece di fs.
 */
export async function getNearbyByRadiusAndHorizonClient(opts: {
  center: { lat: number; lng: number } | null | undefined;
  radiusKm?: number;
  daysAhead?: number;
  excludeId?: string;
}): Promise<SagraListing[]> {
  const { center, radiusKm = 100, daysAhead = 60, excludeId } = opts;
  if (!center || !Number.isFinite(center.lat) || !Number.isFinite(center.lng)) return [];

  const all = await loadGlobalNearbyIndexClient();
  if (all.length === 0) return [];

  const now = Date.now();
  const endTS = now + daysAhead * 86400000;

  const filtered = all
    .filter((e) => {
      // Il nearby-index.json usa date_from/date_to, non startDate/endDate
      const startDate = (e as any).date_from ?? e.startDate;
      const endDate = (e as any).date_to ?? e.endDate;
      if (!startDate) return false;
      const startTS = new Date(startDate).getTime();
      const end = endDate ? new Date(endDate).getTime() : startTS;
      // Finestra temporale: l'evento deve intersecare [oggi, oggi+daysAhead]
      if (end < now || startTS > endTS) return false;
      if (excludeId && e.id === excludeId) return false;
      return true;
    })
    .filter((e) => {
      // Serve geo completo
      const latOk = (e as any).lat ?? (e as any).location?.geo?.lat;
      const lngOk = (e as any).lng ?? (e as any).location?.geo?.lng;
      if (typeof latOk !== "number" || typeof lngOk !== "number") return false;
      const d = haversineDistance({ lat: center.lat, lon: center.lng }, { lat: latOk, lon: lngOk });
      return d <= radiusKm;
    })
    // Ordina dal più vicino
    .sort((a, b) => {
      const aLat = (a as any).lat ?? (a as any).location?.geo?.lat;
      const aLng = (a as any).lng ?? (a as any).location?.geo?.lng;
      const bLat = (b as any).lat ?? (b as any).location?.geo?.lat;
      const bLng = (b as any).lng ?? (b as any).location?.geo?.lng;
      const da = haversineDistance({ lat: center.lat, lon: center.lng }, { lat: aLat, lon: aLng });
      const db = haversineDistance({ lat: center.lat, lon: center.lng }, { lat: bLat, lon: bLng });
      return da - db;
    });

  return filtered;
}

